import firebase from '@firebase/app'
import '@firebase/database'
import Vue from 'vue'
import {
  ADD_COLLECTION,
  ADD_COLLECTION_TO_RECIPE,
  ADD_TO_COLLECTION,
  CLEAR_COLLECTIONS,
  DESTROY_COLLECTION,
  REMOVE_COLLECTION_FROM_RECIPE,
  REMOVE_FROM_COLLECTION,
  UPDATE_COLLECTION,
  ADD_NOTIFICATION } from './mutation-types'

const collectionModel = (key) => {
  return {
    items: {},
    key,
    name: 'Undefined'
  }
}
const getDefaultCollections = () => {
  return {
    library: {
      icon: 'book',
      key: 'library',
      name: 'All Recipes',
      notice: {
        title: 'No Recipes',
        message: 'You haven\'t added any recipes to your\u00a0library. Select the plus button to get\u00a0started.'
      },
      path: '/',
      title: 'Cookmark',
      type: 'system'
    },
    favorites: {
      icon: 'star',
      items: {},
      key: 'favorites',
      name: 'Favorites',
      notice: {
        title: 'No Favorites',
        message: 'Your favorite recipes will appear here.'
      },
      path: '/favorites',
      type: 'system'
    },
    pinned: {
      hidden: true,
      icon: {
        type: 'svg',
        d: 'M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z'
      },
      items: {},
      key: 'pinned',
      name: 'Pinned',
      path: '/pinned',
      type: 'system',
      notice: {
        title: 'Pin recipes you want to access quickly',
        message: 'Pinning lets you jump back to a recipe quickly from anywhere in the app. Pin multiple recipes if you\'re cooking a few things and easily jump between them as you need.\n\n1. Open a recipe in Cookmark\n2. At the top of the recipe, select Pin\n\nPinned recipes appear as a floating button at the bottom-right of your screen.'
      }
    },
    recent: {
      hidden: true,
      icon: 'schedule',
      key: 'recent',
      name: 'Most Recent',
      notice: {
        title: 'No Recent Recipes',
        message: 'Your 20 most recently added recipes will appear\u00a0here.'
      },
      path: '/recent',
      smart: true,
      type: 'system'
    },
    uncategorized: {
      icon: 'inbox',
      key: 'uncategorized',
      name: 'Uncategorized',
      notice: {
        title: 'No Uncategorized Recipes',
        message: 'Any uncategorized recipes will appear here so you can quickly organize your\u00a0library.'
      },
      path: '/uncategorized',
      smart: true,
      type: 'system'
    }
  }
}
const getDefaultState = () => {
  return {
    collections: getDefaultCollections()
  }
}
const mutations = {
  [ADD_COLLECTION] (state, { key, collection }) {
    Vue.set(state.collections, key, Object.assign({},
      collectionModel(key),
      getDefaultCollections()[key],
      collection
    ))
  },
  [DESTROY_COLLECTION] (state, { key }) {
    Vue.delete(state.collections, key)
  },
  [UPDATE_COLLECTION] (state, { key, collection }) {
    Vue.set(state.collections, key, Object.assign({},
      state.collections[key],
      collection
    ))
  },
  [ADD_TO_COLLECTION] (state, { key, collectionKey }) {
    let collection = state.collections[collectionKey]
    if (collection.smart) return
    Vue.set(collection.items, key, true)
  },
  [REMOVE_FROM_COLLECTION] (state, { key, collectionKey }) {
    Vue.delete(state.collections[collectionKey].items, key)
  },
  [CLEAR_COLLECTIONS] (state, payload) {
    Object.assign(state, getDefaultState())
  }
}

const getters = {
  getCollectionById: state => (collectionId) => {
    return state.collections[collectionId]
  },
  userCollections: state => {
    return Object.values(state.collections).filter(collection => {
      return collection.type === 'user' &&
      collection.name !== undefined
    }).sort((a, b) => a.name.localeCompare(b.name))
  },
  systemCollections: state => {
    return Object.values(state.collections).filter(collection => {
      return collection.type === 'system' &&
      collection.name !== undefined &&
      !collection.hidden
    }).sort((a, b) => a.name.localeCompare(b.name))
  }
}

export default {
  state: getDefaultState(),
  actions: {
    fetchCollections ({commit}, {userId}) {
      firebase.database()
        .ref(`users/${userId}/collections`)
        .once('value')
        .then(snapshot => {
          if (!snapshot.exists()) return
          for (const [collectionKey, collection] of Object.entries(snapshot.val())) {
            commit(ADD_COLLECTION, {
              key: collectionKey,
              collection
            })
          }
        })
    },
    attachCollectionListeners ({commit}, {key}) {
      let ref = firebase.database().ref(`users/${key}/collections`)

      ref.limitToLast(1)
        .orderByChild('addedAt')
        .on('child_added', snapshot => {
          commit(ADD_COLLECTION, {
            key: snapshot.key,
            collection: snapshot.val()
          })
        })

      ref.on('child_removed', snapshot => {
        commit(DESTROY_COLLECTION, { key: snapshot.key })
      })

      // TODO:
      // - Prefer to update the record, not re-add to avoid recipe jumping
      //   if being viewed
      // - Currently not performant, reloads all items on change
      // ref.on('child_changed', snapshot => {
      //   commit(ADD_COLLECTION, snapshot)
      // })
    },
    addToCollection ({commit, dispatch, state}, {uid, key, collectionKey, callback}) {
      // Recipes are unable to be added to smart collections
      if (state.collections[collectionKey].smart) return

      const db = firebase.database()
      const userRef = `users/${uid}/`

      db.ref(`${userRef}/collections/${collectionKey}/items/${key}`).set(true, () => {
        commit(ADD_TO_COLLECTION, { key, collectionKey })
        if (callback) callback()
      }).catch(error => {
        console.log(error)
      })

      db.ref(`${userRef}/recipes/${key}/collections/${collectionKey}`).set(true, () => {
        commit(ADD_COLLECTION_TO_RECIPE, { key, collectionKey })
      }).catch(error => {
        console.log(error)
      })
    },
    createCollection ({commit, state}, {uid, name}) {
      const ref = firebase.database().ref(`users/${uid}/collections`).push()
      const key = ref.key
      const collection = {
        addedAt: firebase.database.ServerValue.TIMESTAMP,
        items: [],
        name: name,
        type: 'user'
      }
      return new Promise((resolve, reject) => {
        Object.values(state.collections).forEach(collection => {
          if (collection.name.toUpperCase() === name.toUpperCase()) return reject(new Error('Must be a unique name'))
        })
        ref.set(collection, () => {
          commit(ADD_COLLECTION, { key, collection })
          resolve(key)
        }).catch(error => {
          console.log(error)
          reject(error)
        })
      })
    },
    destroyCollection ({commit, state, dispatch}, {uid, collectionKey}) {
      let collection = this.state.Collection.collections[collectionKey]
      if (collection.type === 'system') {
        return commit(ADD_NOTIFICATION, {
          message: 'Unable to remove permanent collections'
        })
      }
      const collectionRef = `users/${uid}/collections/${collectionKey}`
      return new Promise((resolve, reject) => {
        firebase.database().ref(collectionRef)
          .remove()
          .then(() => {
            commit(ADD_NOTIFICATION, {
              message: 'Removed collection'
            })
            commit(DESTROY_COLLECTION, { key: collectionKey })
            resolve()
          }).catch((error) => {
            commit(ADD_NOTIFICATION, {
              message: 'Unable to remove collection'
            })
            console.log(error)
            reject(error)
          })
      })
    },
    removeFromCollection ({commit}, {uid, key, collectionKey, callback}) {
      var db = firebase.database()
      const userRef = `users/${uid}/`

      db.ref(`${userRef}/collections/${collectionKey}/items/${key}`).remove().then(() => {
        commit(REMOVE_FROM_COLLECTION, { key, collectionKey })
        if (callback) callback()
      }).catch(error => {
        console.log(error)
      })

      db.ref(`${userRef}/recipes/${key}/collections/${collectionKey}`).remove().then(() => {
        commit(REMOVE_COLLECTION_FROM_RECIPE, { key, collectionKey })
      }).catch(error => {
        console.log(error)
      })
    },
    updateCollection ({commit, state}, {uid, key, name}) {
      const collection = {
        name
      }
      const collectionNames = Object.values(state.collections).map(c => c.name)
      const ref = firebase.database().ref(`users/${uid}/collections/${key}`)

      return new Promise((resolve, reject) => {
        if (name === state.collections[key].name) return resolve(collection)
        if (collectionNames.some(collectionName => {
          return collectionName === name
        })) return reject(new Error('Must be a unique name'))

        ref.update(collection, () => {
          commit(UPDATE_COLLECTION, { key, collection })
        }).then(() => {
          commit(ADD_NOTIFICATION, {
            message: 'Updated collection'
          })
          resolve(collection)
        }).catch(error => {
          console.log(error)
          reject(error)
        })
      })
    }

  },
  getters: getters,
  mutations: mutations
}



// WEBPACK FOOTER //
// ./src/modules/Collection.js